import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, authLink } from "@/lib/api";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot
  const [forgotMsg, setForgotMsg] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "coach" ? "/coach" : "/studio");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Unable to sign in.");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError(""); setForgotMsg(null); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot", { email });
      if (data.token) {
        navigate(`/activate?token=${encodeURIComponent(data.token)}&type=reset`);
      } else {
        setForgotMsg("If that email is registered, you can now reset it. If nothing happens, ask your coach for a reset link.");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not start a reset.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left – image / statement */}
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1518998053901-5348d3961a04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Gallery space"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-stone-900/45" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <span className="block h-px w-10 bg-white/80" />
            <span className="font-serif text-2xl">Building The Bridge</span>
          </div>
          <div className="max-w-md">
            <h1 className="font-serif text-5xl font-light leading-tight">Translate lived authority into a values-aligned practice.</h1>
            <p className="mt-5 text-white/80 leading-relaxed">A 16-week, coach-led journey from respected practitioner to clearly positioned advisor – with a tested offer and a sustainable way of working.</p>
          </div>
        </div>
      </div>

      {/* Right – form */}
      <div className="flex items-center justify-center bg-brand-linen px-6 py-16">
        <div className="w-full max-w-sm">
          {mode === "forgot" ? (
            <>
              <div className="label-eyebrow mb-3">Account recovery</div>
              <h2 className="font-serif text-4xl text-stone-900 mb-3">Forgot password</h2>
              <p className="text-sm text-stone-600 mb-7">Enter your email and we'll take you straight to a page to set a new password.</p>
              <form onSubmit={submitForgot} className="space-y-6">
                <div>
                  <label className="text-sm text-stone-600">Email</label>
                  <input
                    data-testid="forgot-email"
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="mt-1 w-full bg-transparent border-b border-stone-300 py-2 text-stone-900 focus:outline-none focus:border-brand-primary transition-colors"
                    placeholder="you@example.com"
                  />
                </div>
                {error && <p data-testid="forgot-error" className="text-sm text-[#9E4A41]">{error}</p>}
                {forgotMsg && <p data-testid="forgot-msg" className="text-sm text-stone-600">{forgotMsg}</p>}
                <button
                  data-testid="forgot-submit"
                  type="submit" disabled={loading}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-sm bg-brand-primary px-5 py-3 text-white transition-all duration-300 hover:bg-brand-hover disabled:opacity-60"
                >
                  {loading ? "Working\u2026" : "Continue"}
                  <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
              <button data-testid="back-to-login" onClick={() => { setMode("login"); setError(""); setForgotMsg(null); }} className="mt-6 text-sm text-stone-500 hover:text-brand-primary transition-colors">
                ← Back to sign in
              </button>
            </>
          ) : (
            <>
              <div className="label-eyebrow mb-3">Welcome back</div>
              <h2 className="font-serif text-4xl text-stone-900 mb-8">Enter your studio</h2>

              <form onSubmit={submit} className="space-y-6">
                <div>
                  <label className="text-sm text-stone-600">Email</label>
                  <input
                    data-testid="login-email"
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="mt-1 w-full bg-transparent border-b border-stone-300 py-2 text-stone-900 focus:outline-none focus:border-brand-primary transition-colors"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="text-sm text-stone-600">Password</label>
                  <input
                    data-testid="login-password"
                    type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                    className="mt-1 w-full bg-transparent border-b border-stone-300 py-2 text-stone-900 focus:outline-none focus:border-brand-primary transition-colors"
                    placeholder="••••••••"
                  />
                </div>

                {error && <p data-testid="login-error" className="text-sm text-[#9E4A41]">{error}</p>}

                <button
                  data-testid="login-submit"
                  type="submit" disabled={loading}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-sm bg-brand-primary px-5 py-3 text-white transition-all duration-300 hover:bg-brand-hover disabled:opacity-60"
                >
                  {loading ? "Signing in\u2026" : "Continue"}
                  <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>

              <button data-testid="forgot-link" onClick={() => { setMode("forgot"); setError(""); }} className="mt-4 text-sm text-stone-500 hover:text-brand-primary transition-colors">
                Forgot password?
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
